import { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import WPInput from "./WPInput";

import { InputStrings } from "../../constants/global.constants";

import { injectValues, isNullOrUndefined } from "../../utils/appUtils";

const DEFAULT_FRACTIONS = 2;

export const validationRangeHardError = (t, value, range) => {
  const [min, max] = range;
  if (value < min || value > max) {
    return {
      error: injectValues(InputStrings(t).outOfRange, { min, max }),
    };
  }
};

export const validationRangeSoftWarning = (t, value, range) => {
  const [min, max] = range || [];
  if (isNullOrUndefined(min) || isNullOrUndefined(max)) {
    return null;
  }

  if (value < min || value > max) {
    return {
      warning: injectValues(InputStrings(t).softRange, { min, max }),
    };
  }
};
export const validationRangeDefault = (t, range, hasSoftRange) => {
  if (!range) return;
  const [min, max] = range;
  const stringKey = hasSoftRange ? "softRange" : "hardRange";
  return {
    default: injectValues(InputStrings(t)[stringKey], { min, max }),
  };
};

const RangedInput = (props, ref) => {
  const { ranges, integer, fractions, skipValidation, ...inputProps } = props;
  const { t } = useTranslation();

  const roundOff = useMemo(
    () => (integer ? 0 : (fractions ?? DEFAULT_FRACTIONS)),
    [fractions],
  );

  const rangeValidation = useCallback(
    ({ ranges, value }, validateOnBlur) => {
      const { soft, hard } = ranges || {};

      if (!soft && !hard) return null;

      const val = Number(value);
      const hardMax = hard?.max ?? hard?.maxValue;
      const hardMin = hard?.min ?? hard?.minValue;

      const softMin = soft?.min ?? soft?.minValue;
      const softMax = soft?.max ?? soft?.maxValue;
      const hasSoftLimit = softMin && softMax;

      if (
        ((!isNullOrUndefined(value) && value !== "") || validateOnBlur) &&
        !skipValidation
      ) {
        const hardError = validationRangeHardError(t, val, [hardMin, hardMax]);

        if (hardError) {
          return hardError;
        }
        const softWarning = validationRangeSoftWarning(
          t,
          val,
          [softMin, softMax],
          hasSoftLimit,
        );

        if (softWarning) {
          return softWarning;
        }
      }

      const defaultRange = hasSoftLimit
        ? [softMin, softMax]
        : [hardMin, hardMax];

      return validationRangeDefault(t, defaultRange, hasSoftLimit);
    },
    [t],
  );

  const inputRangeValidation = useCallback(
    (value, validateOnBlur) =>
      rangeValidation({ ranges, value }, validateOnBlur),
    [ranges, rangeValidation],
  );

  const rangeHelperText = useMemo(() => {
    const { soft, hard } = ranges || {};
    const softMin = soft?.min ?? soft?.minValue;
    const softMax = soft?.max ?? soft?.maxValue;
    const hardMin = hard?.min ?? hard?.minValue;
    const hardMax = hard?.max ?? hard?.maxValue;

    const hasSoftRange =
      !isNullOrUndefined(softMin) && !isNullOrUndefined(softMax);
    const hasHardRange =
      !isNullOrUndefined(hardMin) && !isNullOrUndefined(hardMax);

    if (!hasSoftRange && !hasHardRange) {
      return inputProps.helperText;
    }

    const [min, max] = hasSoftRange ? [softMin, softMax] : [hardMin, hardMax];
    const stringKey = hasSoftRange ? "softRange" : "hardRange";

    return injectValues(InputStrings(t)[stringKey], { min, max });
  }, [ranges, inputProps.helperText, t]);

  return (
    <WPInput
      ref={ref}
      type="number"
      roundOff={roundOff}
      allowDecimals={!integer}
      validate={inputRangeValidation}
      helperText={rangeHelperText}
      {...inputProps}
    />
  );
};

export const WPRangedInput = forwardRef(RangedInput);
